
import React, { useCallback, useState } from 'react';
import { ProjectData, ContactEntry } from '../../types';
import { Building, MapPin, User, HardHat, Calendar, Upload, Image as ImageIcon, X, Briefcase, FileText, ChevronDown, ChevronUp, Loader2, Users, Plus, Pencil, Trash2, Mail, Phone, RefreshCw } from 'lucide-react';
import { AddressAutocomplete } from './UfsaAddressAutocomplete';

interface ProjectFormProps {
  data: ProjectData;
  onChange: (key: keyof ProjectData, value: any) => void;
  defaultPreamble?: string;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({ data, onChange, defaultPreamble }) => {
  const [dragTarget, setDragTarget] = useState<'project' | 'logo' | null>(null);
  const [isPreambleExpanded, setIsPreambleExpanded] = useState(false);
  const [isContactsExpanded, setIsContactsExpanded] = useState(true);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [newContact, setNewContact] = useState<Partial<ContactEntry>>({});
  // Leer = Neuanlage, sonst die id des Kontakts, der gerade im Formular liegt.
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  const handleDrag = useCallback((e: React.DragEvent, target: 'project' | 'logo') => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragTarget(target);
    } else if (e.type === 'dragleave') {
      setDragTarget(null);
    }
  }, []);

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1024; 
          const MAX_HEIGHT = 1024;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error("Canvas context failed"));
            return;
          }
          
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.7)); 
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  const processFile = async (file: File, field: keyof ProjectData) => {
    if (file && file.type.startsWith('image/')) {
      setIsProcessingImage(true);
      try {
        // Nutzt die LOKALE compressImage oben, die nur skaliert und nichts
        // zuschneidet — nicht die aus services/imageService.
        const compressedBase64 = await compressImage(file);
        onChange(field, compressedBase64);
      } catch (err) {
        console.error("Image processing failed", err);
        alert("Fehler beim Verarbeiten des Bildes. Bitte versuchen Sie ein anderes Bild.");
      } finally {
        setIsProcessingImage(false);
      }
    }
  };

  const handleDrop = useCallback((e: React.DragEvent, target: 'project' | 'logo') => {
    e.preventDefault();
    e.stopPropagation();
    setDragTarget(null);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const field = target === 'project' ? 'projectImage' : 'companyLogo';
      processFile(e.dataTransfer.files[0], field);
    }
  }, [onChange]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, field: keyof ProjectData) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0], field);
    }
  };

  const saveContact = () => {
    if (!newContact.role || !newContact.company) {
      alert("Bitte mindestens Rolle und Firma angeben.");
      return;
    }
    const existing = data.contacts || [];

    if (editingContactId) {
      // Nur die Formularfelder ueberschreiben: present und emailDistribution
      // werden anderswo gepflegt (Teilnehmer, E-Mail-Verteiler) und duerfen
      // beim Korrigieren einer Anschrift nicht zurueckfallen.
      const updatedContacts = existing.map(c => c.id === editingContactId ? {
        ...c,
        role: newContact.role || '',
        company: newContact.company || '',
        name: newContact.name || '',
        address: newContact.address || '',
        phone: newContact.phone || '',
        email: newContact.email || ''
      } : c);
      onChange('contacts', updatedContacts);
    } else {
      const contact: ContactEntry = {
        id: crypto.randomUUID(),
        role: newContact.role || '',
        company: newContact.company || '',
        name: newContact.name || '',
        address: newContact.address || '',
        phone: newContact.phone || '',
        email: newContact.email || '',
        present: false,
        emailDistribution: false
      };
      onChange('contacts', [...existing, contact]);
    }

    setNewContact({});
    setEditingContactId(null);
  };

  const startEditContact = (contact: ContactEntry) => {
    setEditingContactId(contact.id);
    setNewContact({
      role: contact.role,
      company: contact.company,
      name: contact.name,
      address: contact.address,
      phone: contact.phone,
      email: contact.email
    });
  };

  const cancelEditContact = () => {
    setEditingContactId(null);
    setNewContact({});
  };

  const removeContact = (id: string) => {
    const updatedContacts = (data.contacts || []).filter(c => c.id !== id);
    onChange('contacts', updatedContacts);
    // Sonst zeigt das Formular weiter auf einen Kontakt, den es nicht mehr gibt.
    if (editingContactId === id) cancelEditContact();
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-md border border-slate-100 mb-8 relative">
      <div className="absolute top-0 left-0 w-full h-1 bg-slate-200 rounded-t-xl overflow-hidden">
        <div className="h-full bg-blue-600 w-1/3"></div>
      </div>
      
      {/* SECTION 1: PROJEKT */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Building className="w-5 h-5 text-blue-600" />
            Projektstammdaten
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Inputs */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label htmlFor="project-name" className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1">
                Bauvorhaben
              </label>
              <input
                id="project-name"
                type="text"
                value={data.projectName}
                onChange={(e) => onChange('projectName', e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="z.B. Neubau Bürokomplex..."
              />
            </div>

            {/* Standort Autocomplete */}
            <AddressAutocomplete
              id="project-location"
              label="Standort"
              labelIcon={<MapPin className="w-3 h-3" />}
              value={data.location}
              onChange={(val) => onChange('location', val)}
              placeholder="Straße, PLZ, Ort suchen..."
            />

            <div className="space-y-1.5">
              <label htmlFor="project-builder" className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1">
                <User className="w-3 h-3" /> Bauherr
              </label>
              <input
                id="project-builder"
                type="text"
                value={data.builder}
                onChange={(e) => onChange('builder', e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="Name des Bauherrn"
              />
            </div>

            {/* Bauherr Address Autocomplete */}
            <AddressAutocomplete
              label="Bauherr Anschrift"
              labelIcon={<MapPin className="w-3 h-3" />}
              value={data.builderAddress || ''}
              onChange={(val) => onChange('builderAddress', val)}
              placeholder="Adresse des Bauherrn..."
            />

            <div className="space-y-1.5">
              <label htmlFor="project-coordinator" className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1">
                <HardHat className="w-3 h-3" /> Koordinator (SiGeKo)
              </label>
              <input
                id="project-coordinator"
                type="text"
                value={data.coordinator}
                onChange={(e) => onChange('coordinator', e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="Name des Koordinators"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Stand vom
              </label>
              <input
                type="date"
                value={data.date}
                onChange={(e) => onChange('date', e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>

            <div className="space-y-1.5 flex flex-col justify-end">
               <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1">
                 Status
               </label>
               <div className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-lg bg-slate-50">
                  <input
                    type="checkbox"
                    id="isDraft"
                    checked={data.isDraft || false}
                    onChange={(e) => onChange('isDraft', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300 cursor-pointer"
                  />
                  <label htmlFor="isDraft" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Entwurf (Wasserzeichen anzeigen)
                  </label>
               </div>
            </div>
          </div>

          {/* Image Upload: Project */}
          <div className="lg:col-span-1">
            <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1 mb-1.5">
              <ImageIcon className="w-3 h-3" /> Projektbild (Deckblatt)
            </label>
            
            {!data.projectImage ? (
              <div
                onDragEnter={(e) => handleDrag(e, 'project')}
                onDragLeave={(e) => handleDrag(e, 'project')}
                onDragOver={(e) => handleDrag(e, 'project')}
                onDrop={(e) => handleDrop(e, 'project')}
                className={`
                  h-44 border-2 border-dashed rounded-xl flex flex-col items-center justify-center text-center p-4 transition-all cursor-pointer relative
                  ${dragTarget === 'project' ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}
                `}
              >
                {isProcessingImage ? (
                   <div className="flex flex-col items-center text-blue-600">
                     <Loader2 className="w-8 h-8 animate-spin mb-2" />
                     <span className="text-xs font-medium">Optimiere Bild...</span>
                   </div>
                ) : (
                  <>
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      id="project-image-upload"
                      onChange={(e) => handleFileSelect(e, 'projectImage')}
                    />
                    <label htmlFor="project-image-upload" className="cursor-pointer flex flex-col items-center w-full h-full justify-center">
                      <div className={`p-3 rounded-full mb-2 ${dragTarget === 'project' ? 'bg-blue-100' : 'bg-slate-100'}`}>
                         <Upload className={`w-6 h-6 ${dragTarget === 'project' ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                      <span className="text-sm text-slate-600 font-medium">Bild hier ablegen</span>
                      <span className="text-xs text-slate-400 mt-1">oder klicken zum Auswählen</span>
                    </label>
                  </>
                )}
              </div>
            ) : (
              <div className="relative h-44 rounded-xl overflow-hidden border border-slate-200 group shadow-sm">
                <img 
                  src={data.projectImage} 
                  alt="Projekt" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/60 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                  <button
                    onClick={() => onChange('projectImage', '')}
                    className="bg-red-500 hover:bg-red-600 text-white p-2.5 rounded-full transform scale-90 group-hover:scale-100 transition-transform shadow-lg"
                    title="Bild entfernen"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* SECTION 2: WEITERE BETEILIGTE (NEU) */}
      <div id="section-contacts" className="pt-8 border-t border-slate-100 mb-6">
        <button
            type="button"
            onClick={() => setIsContactsExpanded(!isContactsExpanded)}
            className="w-full flex justify-between items-center group focus:outline-none p-2 -ml-2 rounded-lg hover:bg-slate-50 transition-colors mb-4"
         >
            <div className="text-left flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isContactsExpanded ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'} transition-colors`}>
                   <Users className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors">Adressverzeichnis / Beteiligte</h2>
                    <p className="text-xs text-slate-400">Planer, Fachbauleiter und ausführende Firmen (Seite 2 der PDF)</p>
                </div>
            </div>
            <div className={`p-2 rounded-full bg-slate-100 group-hover:bg-blue-100 transition-colors ${isContactsExpanded ? 'text-blue-600' : 'text-slate-400'}`}>
                {isContactsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
         </button>

         {isContactsExpanded && (
           <div className="animate-in slide-in-from-top-2 duration-200">
              
              {/* Existing List */}
              {data.contacts && data.contacts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {data.contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className={`border rounded-lg p-4 flex justify-between items-start gap-2 group transition-colors ${
                        editingContactId === contact.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-slate-50 hover:border-blue-200'
                      }`}
                    >
                       <div className="min-w-0">
                          <div className="text-xs font-bold text-blue-600 uppercase mb-1">{contact.role}</div>
                          <div className="font-bold text-slate-800">{contact.company}</div>
                          {contact.name && <div className="text-sm text-slate-600 flex items-center gap-1 mt-1"><User className="w-3 h-3 text-slate-400 shrink-0" /> {contact.name}</div>}
                          {contact.address && <div className="text-sm text-slate-500 flex items-start gap-1"><MapPin className="w-3 h-3 text-slate-400 shrink-0 mt-1" /> <span className="break-words">{contact.address}</span></div>}
                          {contact.phone && <div className="text-sm text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400 shrink-0" /> {contact.phone}</div>}
                          {contact.email && <div className="text-sm text-slate-500 flex items-center gap-1 min-w-0"><Mail className="w-3 h-3 text-slate-400 shrink-0" /> <span className="truncate">{contact.email}</span></div>}
                       </div>
                       <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => startEditContact(contact)}
                            title="Kontakt bearbeiten"
                            aria-label={`Kontakt ${contact.company} bearbeiten`}
                            className="text-slate-400 hover:text-blue-600 p-1 hover:bg-blue-50 rounded transition-colors"
                          >
                             <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeContact(contact.id)}
                            title="Kontakt löschen"
                            aria-label={`Kontakt ${contact.company} löschen`}
                            className="text-slate-400 hover:text-red-500 p-1 hover:bg-red-50 rounded transition-colors"
                          >
                             <Trash2 className="w-4 h-4" />
                          </button>
                       </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Form */}
              <div className="bg-blue-50/50 p-5 rounded-xl border border-blue-100">
                <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase flex items-center gap-2">
                   {editingContactId
                     ? <><Pencil className="w-4 h-4" /> Kontakt bearbeiten</>
                     : <><Plus className="w-4 h-4" /> Neuen Kontakt hinzufügen</>}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                   <input 
                      placeholder="Rolle (z.B. Architekt, Dachdecker)" 
                      className="p-2 border border-slate-300 rounded text-sm"
                      value={newContact.role || ''}
                      onChange={e => setNewContact({...newContact, role: e.target.value})}
                   />
                   <input 
                      placeholder="Firma" 
                      className="p-2 border border-slate-300 rounded text-sm"
                      value={newContact.company || ''}
                      onChange={e => setNewContact({...newContact, company: e.target.value})}
                   />
                   <input 
                      placeholder="Ansprechpartner (Name)" 
                      className="p-2 border border-slate-300 rounded text-sm"
                      value={newContact.name || ''}
                      onChange={e => setNewContact({...newContact, name: e.target.value})}
                   />
                   <input 
                      placeholder="Anschrift (Straße, PLZ, Ort)" 
                      className="p-2 border border-slate-300 rounded text-sm"
                      value={newContact.address || ''}
                      onChange={e => setNewContact({...newContact, address: e.target.value})}
                   />
                   <input 
                      placeholder="Telefon" 
                      className="p-2 border border-slate-300 rounded text-sm"
                      value={newContact.phone || ''}
                      onChange={e => setNewContact({...newContact, phone: e.target.value})}
                   />
                   <input 
                      placeholder="E-Mail" 
                      className="p-2 border border-slate-300 rounded text-sm"
                      value={newContact.email || ''}
                      onChange={e => setNewContact({...newContact, email: e.target.value})}
                   />
                </div>
                <div className="flex justify-end gap-2">
                   {editingContactId && (
                     <button
                        type="button"
                        onClick={cancelEditContact}
                        className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                     >
                        Abbrechen
                     </button>
                   )}
                   <button
                      type="button"
                      onClick={saveContact}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition-colors"
                   >
                      {editingContactId ? 'Änderungen speichern' : 'Hinzufügen'}
                   </button>
                </div>
              </div>
           </div>
         )}
      </div>

      {/* SECTION 3: UNTERNEHMEN */}
      <div className="pt-6 border-t border-slate-100 mb-6">
        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-600" />
            Ersteller / Unternehmen
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
             <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1">
                Unternehmen
              </label>
              <input
                type="text"
                value={data.companyName || ''}
                onChange={(e) => onChange('companyName', e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="Firmenname..."
              />
            </div>
            
             {/* Company Address Autocomplete */}
             <AddressAutocomplete
               label="Anschrift"
               labelIcon={<MapPin className="w-3 h-3" />}
               value={data.companyAddress || ''}
               onChange={(val) => onChange('companyAddress', val)}
               placeholder="Adresse suchen..."
             />
          </div>

          {/* Image Upload: Company Logo */}
          <div className="lg:col-span-1">
            <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1 mb-1.5">
              <ImageIcon className="w-3 h-3" /> Firmenlogo
            </label>
            
            {!data.companyLogo ? (
              <div
                onDragEnter={(e) => handleDrag(e, 'logo')}
                onDragLeave={(e) => handleDrag(e, 'logo')}
                onDragOver={(e) => handleDrag(e, 'logo')}
                onDrop={(e) => handleDrop(e, 'logo')}
                className={`
                  h-32 border-2 border-dashed rounded-xl flex flex-col items-center justify-center text-center p-4 transition-all cursor-pointer relative
                  ${dragTarget === 'logo' ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'}
                `}
              >
                {isProcessingImage ? (
                   <div className="flex flex-col items-center text-blue-600 scale-75">
                     <Loader2 className="w-6 h-6 animate-spin mb-1" />
                     <span className="text-[11px] font-medium">Verarbeite...</span>
                   </div>
                ) : (
                  <>
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      id="logo-upload"
                      onChange={(e) => handleFileSelect(e, 'companyLogo')}
                    />
                    <label htmlFor="logo-upload" className="cursor-pointer flex flex-col items-center w-full h-full justify-center">
                      <div className={`p-2 rounded-full mb-2 ${dragTarget === 'logo' ? 'bg-blue-100' : 'bg-slate-100'}`}>
                         <Upload className={`w-5 h-5 ${dragTarget === 'logo' ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                      <span className="text-xs text-slate-600 font-medium">Logo ablegen</span>
                    </label>
                  </>
                )}
              </div>
            ) : (
              <div className="relative h-32 rounded-xl overflow-hidden border border-slate-200 group bg-slate-50 flex items-center justify-center shadow-sm">
                <img 
                  src={data.companyLogo} 
                  alt="Firmenlogo" 
                  className="max-w-full max-h-full object-contain p-3"
                />
                <div className="absolute inset-0 bg-slate-900/60 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                  <button
                    onClick={() => onChange('companyLogo', '')}
                    className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transform scale-90 group-hover:scale-100 transition-transform shadow-lg"
                    title="Logo entfernen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SECTION 4: VORBEMERKUNGEN / TEXT (Collapsible) */}
      <div className="pt-6 border-t border-slate-100">
         <button
            type="button"
            onClick={() => setIsPreambleExpanded(!isPreambleExpanded)}
            className="w-full flex justify-between items-center group focus:outline-none p-2 -ml-2 rounded-lg hover:bg-slate-50 transition-colors"
         >
            <div className="text-left flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isPreambleExpanded ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'} transition-colors`}>
                   <FileText className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-800 group-hover:text-blue-600 transition-colors">Vorbemerkungen / Projektbeschreibung</h2>
                    <p className="text-xs text-slate-400">Rechtliche Hinweise und Beschreibung für Seite 3</p>
                </div>
            </div>
            <div className={`p-2 rounded-full bg-slate-100 group-hover:bg-blue-100 transition-colors ${isPreambleExpanded ? 'text-blue-600' : 'text-slate-400'}`}>
                {isPreambleExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
         </button>
         
         {isPreambleExpanded && (
             <div className="mt-6 animate-in slide-in-from-top-2 duration-200 pl-2">
                <div className="space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1">
                        Inhalt bearbeiten
                      </label>
                      {defaultPreamble && (
                           <button 
                              type="button"
                              onClick={() => {
                                  if (window.confirm("Möchten Sie den Text wirklich auf den Standard zurücksetzen? Ihre Änderungen gehen verloren.")) {
                                      onChange('preamble', defaultPreamble);
                                  }
                              }}
                              className="text-[11px] flex items-center gap-1 bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 transition-colors font-medium"
                           >
                               <RefreshCw className="w-3 h-3" /> Standardtext laden
                           </button>
                      )}
                    </div>
                    <div className="relative">
                        <textarea
                            value={data.preamble}
                            onChange={(e) => onChange('preamble', e.target.value)}
                            className="w-full p-5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono text-sm leading-relaxed bg-slate-50 min-h-[300px]"
                            placeholder="Fügen Sie hier die Vorbemerkungen ein..."
                        />
                        <div className="absolute top-2 right-2 px-2 py-1 bg-white/80 backdrop-blur rounded text-[11px] text-slate-400 border border-slate-200 pointer-events-none">
                            Markdown: 1.1 für Headlines, • für Listen, [UMBRUCH] für neue Seite
                        </div>
                    </div>
                </div>
             </div>
         )}
      </div>

    </div>
  );
};
